'use client';
import React, { useState, useEffect, useCallback } from 'react';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  location: string;
  quote: string;
  avatar: string;
  initials: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Priya Sharma',
    role: 'Wedding Planner',
    location: 'Bhopal, MP',
    quote:
      'Rani Kamlapati transformed how we manage royal-scale weddings. The platform\'s elegance matches the grandeur of every event we host. Booking 500+ guests was seamless — a true heritage experience.',
    avatar: 'PS',
    initials: 'PS',
  },
  {
    id: 2,
    name: 'Arjun Mehta',
    role: 'Corporate Event Director',
    location: 'Indore, MP',
    quote:
      'The regal interface and smooth ticketing made our annual gala feel truly distinguished. Our attendees noticed the difference immediately.',
    avatar: 'AM',
    initials: 'AM',
  },
  {
    id: 3,
    name: 'Sunita Rao',
    role: 'Cultural Festival Organizer',
    location: 'Jabalpur, MP',
    quote:
      'From classical music concerts to heritage walks, this platform captures the soul of Madhya Pradesh\'s rich culture. Absolutely magnificent.',
    avatar: 'SR',
    initials: 'SR',
  },
  {
    id: 4,
    name: 'Vikram Tiwari',
    role: 'Venue Manager',
    location: 'Gwalior, MP',
    quote:
      'Managing bookings for our historic fort venue has never been this refined. The gold-accented dashboard feels as majestic as the venue itself.',
    avatar: 'VT',
    initials: 'VT',
  },
  {
    id: 5,
    name: 'Meera Joshi',
    role: 'Art Exhibition Curator',
    location: 'Ujjain, MP',
    quote:
      'Our patrons expect nothing but the finest. Rani Kamlapati delivers that prestige in every notification, every ticket, every interaction.',
    avatar: 'MJ',
    initials: 'MJ',
  },
  {
    id: 6,
    name: 'Rajesh Pandey',
    role: 'Classical Musician',
    location: 'Rewa, MP',
    quote:
      'As a performer, I need my audience to arrive informed and inspired. This platform\'s elegant communication sets the perfect tone before the curtain even rises.',
    avatar: 'RP',
    initials: 'RP',
  },
  {
    id: 7,
    name: 'Ananya Dubey',
    role: 'Heritage Tourism Director',
    location: 'Orchha, MP',
    quote:
      'We host guests from across the globe at our palace properties. Rani Kamlapati\'s sophisticated booking flow matches the grandeur our visitors expect.',
    avatar: 'AD',
    initials: 'AD',
  },
  {
    id: 8,
    name: 'Suresh Malhotra',
    role: 'Theatre Producer',
    location: 'Sagar, MP',
    quote:
      'Selling out our 800-seat auditorium used to be a logistical challenge. Now it\'s a pleasure — the platform handles everything with royal grace.',
    avatar: 'SM',
    initials: 'SM',
  },
  {
    id: 9,
    name: 'Kavita Nair',
    role: 'Luxury Event Stylist',
    location: 'Satna, MP',
    quote:
      'Every detail matters in luxury events. Rani Kamlapati understands this philosophy — from the ornate design to the seamless guest experience.',
    avatar: 'KN',
    initials: 'KN',
  },
];

// Groups of 3: [featured, secondary, tertiary]
const GROUPS = Math.ceil(testimonials.length / 3);

function AvatarBadge({ initials, size = 'md' }: { initials: string; size?: 'sm' | 'md' | 'lg' }) {
  const sizeMap = {
    sm: 'w-10 h-10 text-sm',
    md: 'w-14 h-14 text-base',
    lg: 'w-20 h-20 text-xl',
  };
  return (
    <div
      className={`${sizeMap[size]} rounded-full flex items-center justify-center font-bold flex-shrink-0`}
      style={{
        background: 'linear-gradient(135deg, #D4AF37 0%, #9B7D1A 100%)',
        color: '#071C18',
        letterSpacing: '0.05em',
      }}
    >
      {initials}
    </div>
  );
}

function FeaturedCard({ testimonial, visible }: { testimonial: Testimonial; visible: boolean }) {
  return (
    <div
      className="relative rounded-2xl overflow-hidden flex flex-col justify-between h-full"
      style={{
        background: 'linear-gradient(145deg, #0D2E26 0%, #071C18 60%, #0A2218 100%)',
        border: '1px solid rgba(212,175,55,0.35)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 0.5s cubic-bezier(0.4,0,0.2,1), transform 0.5s cubic-bezier(0.4,0,0.2,1)',
        minHeight: '340px',
        padding: '2.5rem',
      }}
    >
      {/* Decorative large quote */}
      <div
        className="absolute top-4 right-6 select-none pointer-events-none"
        style={{
          fontSize: '9rem',
          lineHeight: 1,
          color: '#D4AF37',
          opacity: 0.08,
          fontWeight: 700,
          fontFamily: 'Inter, sans-serif',
        }}
      >
        &ldquo;
      </div>

      {/* Gold accent line */}
      <div
        className="absolute top-0 left-8 right-8 h-0.5 rounded-full"
        style={{ background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)' }}
      />

      <div className="relative z-10 flex flex-col h-full">
        {/* Opening quote mark */}
        <div
          className="text-4xl leading-none mb-4 select-none"
          style={{ color: '#D4AF37', opacity: 0.6, fontFamily: 'Inter, sans-serif' }}
        >
          &ldquo;
        </div>

        <blockquote
          className="text-lg leading-relaxed flex-1 mb-8"
          style={{
            color: '#F7F1E3',
            fontStyle: 'italic',
            fontWeight: 300,
            letterSpacing: '0.01em',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {testimonial.quote}
        </blockquote>

        <div className="flex items-center gap-4 mt-auto">
          <AvatarBadge initials={testimonial.initials} size="lg" />
          <div>
            <p className="font-semibold text-base" style={{ color: '#F7F1E3', fontFamily: 'Inter, sans-serif' }}>
              {testimonial.name}
            </p>
            <p className="text-sm mt-0.5" style={{ color: '#D4AF37', opacity: 0.8, fontFamily: 'Inter, sans-serif' }}>
              {testimonial.role}
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#F7F1E3', opacity: 0.45, fontFamily: 'Inter, sans-serif' }}>
              {testimonial.location}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompactCard({
  testimonial,
  visible,
  delay,
}: {
  testimonial: Testimonial;
  visible: boolean;
  delay: number;
}) {
  return (
    <div
      className="relative rounded-xl overflow-hidden flex flex-col justify-between"
      style={{
        background: 'rgba(212,175,55,0.06)',
        border: '1px solid rgba(212,175,55,0.2)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(20px)',
        transition: `opacity 0.5s cubic-bezier(0.4,0,0.2,1) ${delay}ms, transform 0.5s cubic-bezier(0.4,0,0.2,1) ${delay}ms`,
        padding: '1.5rem',
        flex: 1,
      }}
    >
      {/* Side accent */}
      <div
        className="absolute left-0 top-4 bottom-4 w-0.5 rounded-full"
        style={{ background: 'linear-gradient(180deg, transparent, #D4AF37, transparent)' }}
      />

      <blockquote
        className="text-sm leading-relaxed mb-4 pl-3"
        style={{ color: '#F7F1E3', opacity: 0.85, fontStyle: 'italic', fontFamily: 'Inter, sans-serif' }}
      >
        &ldquo;{testimonial.quote}&rdquo;
      </blockquote>

      <div className="flex items-center gap-3 pl-3">
        <AvatarBadge initials={testimonial.initials} size="sm" />
        <div>
          <p className="font-semibold text-sm" style={{ color: '#F7F1E3', fontFamily: 'Inter, sans-serif' }}>
            {testimonial.name}
          </p>
          <p className="text-xs mt-0.5" style={{ color: '#D4AF37', opacity: 0.75, fontFamily: 'Inter, sans-serif' }}>
            {testimonial.role} · {testimonial.location}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  const [groupIndex, setGroupIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const totalGroups = GROUPS;

  const changeGroup = useCallback(
    (next: number) => {
      setVisible(false);
      setTimeout(() => {
        setGroupIndex(next);
        setVisible(true);
      }, 350);
    },
    []
  );

  const nextGroup = useCallback(() => {
    changeGroup((groupIndex + 1) % totalGroups);
  }, [groupIndex, totalGroups, changeGroup]);

  const prevGroup = useCallback(() => {
    changeGroup((groupIndex - 1 + totalGroups) % totalGroups);
  }, [groupIndex, totalGroups, changeGroup]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextGroup, 5000);
    return () => clearInterval(timer);
  }, [nextGroup, isPaused]);

  const base = groupIndex * 3;
  const featured = testimonials[base % testimonials.length];
  const secondary = testimonials[(base + 1) % testimonials.length];
  const tertiary = testimonials[(base + 2) % testimonials.length];

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #071C18 0%, #0D2E26 50%, #071C18 100%)' }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Subtle noise texture */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '128px',
        }}
      />

      {/* Ambient glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(212,175,55,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <p
              className="text-xs tracking-[0.4em] uppercase mb-3"
              style={{ color: '#D4AF37', fontFamily: 'DM Sans, sans-serif' }}
            >
              Voices of Distinction
            </p>
            <h2
              className="text-4xl md:text-5xl font-bold leading-tight"
              style={{ color: '#F7F1E3', fontFamily: 'Fraunces, Georgia, serif', letterSpacing: '-0.01em' }}
            >
              What Our
              <br />
              <span style={{ color: '#D4AF37' }}>Patrons Say</span>
            </h2>
          </div>

          {/* Navigation controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={prevGroup}
              aria-label="Previous"
              className="w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200"
              style={{
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.3)',
                color: '#D4AF37',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.25)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.1)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Dot indicators */}
            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalGroups }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => changeGroup(i)}
                  aria-label={`Group ${i + 1}`}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === groupIndex ? '24px' : '6px',
                    height: '6px',
                    background: i === groupIndex ? '#D4AF37' : 'rgba(212,175,55,0.3)',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>

            <button
              onClick={nextGroup}
              aria-label="Next"
              className="w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200"
              style={{
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.3)',
                color: '#D4AF37',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.25)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.1)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Asymmetric layout: 60/40 split */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Featured card — spans 3 cols */}
          <div className="lg:col-span-3">
            <FeaturedCard testimonial={featured} visible={visible} />
          </div>

          {/* Two stacked compact cards — spans 2 cols */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <CompactCard testimonial={secondary} visible={visible} delay={80} />
            <CompactCard testimonial={tertiary} visible={visible} delay={160} />
          </div>
        </div>

        {/* Stats strip */}
        <div
          className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden rounded-xl"
          style={{ background: 'rgba(212,175,55,0.15)' }}
        >
          {[
            { value: '10,000+', label: 'Happy Patrons' },
            { value: '4.9 / 5', label: 'Average Rating' },
            { value: '500+', label: 'Events Hosted' },
            { value: '98%', label: 'Satisfaction Rate' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="text-center py-5 px-4"
              style={{ background: '#071C18' }}
            >
              <p
                className="text-2xl font-bold"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37, #F0D060)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {stat.value}
              </p>
              <p className="text-xs mt-1" style={{ color: '#F7F1E3', opacity: 0.5 }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
